import {
  createAsyncThunk,
  createReducer,
} from '@reduxjs/toolkit'

import { NormalStatus } from '@/constants/normal-status'
import {
  MAX_PAGE_SIZE,
  Pagination,
  PaginationQuery,
} from '@/constants/pagination'
import { Role } from '@/model/role'
import { axios } from '@/utils/axios'
import {
  removeItem,
  updateOrAddArray,
  updateOrAddArrayItem,
} from '@/utils/draft'

import { createAppAsyncThunk } from './utils'

const namespace = 'role'

export interface SearchRolesQuery extends PaginationQuery {
  name?: string;
  status?: NormalStatus;
}

export const readAllRoles = createAppAsyncThunk(
  `${namespace}/readAllRoles`,
  async (
    params: SearchRolesQuery | void = { pageNum: 1, pageSize: MAX_PAGE_SIZE },
  ) => {
    return (
      await axios.get<Pagination<Role>>('/role/all', {
        params,
      })
    ).data
  },
)
export const readMineRoles = createAppAsyncThunk(
  `${namespace}/readMineRoles`,
  async () => (await axios.get<Role[]>('/role/mine')).data,
)
export type CreateRoleParams = Omit<
  Role,
  'id' | 'employeeIds' | 'functionIds' | 'menuPermissions' | 'isSystemDefault'
>;
export const createRole = createAsyncThunk(
  `${namespace}/createRole`,
  async (data: CreateRoleParams) => (await axios.post<Role>('/role', data)).data,
)
export type UpdateRoleParams = Partial<Omit<Role, 'employeeIds'>>;
export const updateRole = createAsyncThunk(
  `${namespace}/updateRole`,
  async (data: UpdateRoleParams) =>
    (await axios.put<Role>(`/role/${data.id}`, data)).data,
)
export const removeRole = createAsyncThunk(
  `${namespace}/removeRole`,
  async (id: number) => await axios.delete(`/role/${id}`),
)

const initialState: {
  mineRoles: Role[];
  roles: Role[];
} = {
  mineRoles: [],
  roles: [],
}

const reducer = createReducer(initialState, (builder) => {
  builder.addCase(readAllRoles.fulfilled, (draft, { payload }) => {
    updateOrAddArray(payload.rows, draft.roles)
  })
  builder.addCase(readMineRoles.fulfilled, (draft, { payload }) => {
    draft.mineRoles = payload
    updateOrAddArray(payload, draft.roles)
  })
  builder.addCase(createRole.fulfilled, (draft, { payload }) => {
    draft.roles.push(payload)
  })
  builder.addCase(updateRole.fulfilled, (draft, { payload }) => {
    updateOrAddArrayItem(payload, draft.roles)
  })
  builder.addCase(removeRole.fulfilled, (draft, { meta }) => {
    removeItem(meta.arg, draft.roles)
  })
})

export default { [namespace]: reducer }
